import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Loader2, Plus, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { NuovoEventoDialog } from '../components/eventi/NuovoEventoDialog';
import { TesseraData } from '../components/eventi/TesseraData';
import { useNotifications } from '../store/useStore';
import { eventiService } from '../lib/eventi/eventiService';
import { messaggioErrore } from '../lib/eventi/errori';
import { periodoEvento } from '../lib/eventi/riepilogo';
import { cn } from '../lib/cn';
import type { EventoInElenco } from '../lib/eventi/tipi';

/** Un evento nell'elenco: quando, quanti partecipano, se le richieste sono aperte, se manca il programma. */
function SchedaEvento({ evento: e, onApri }: { evento: EventoInElenco; onApri: () => void }) {
  const giorni = Array.isArray(e.giorni) ? e.giorni : [];
  const periodo = periodoEvento(giorni);
  return (
    <button
      type="button"
      onClick={onApri}
      className="group flex flex-col rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:border-gray-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <span className="flex items-start gap-3 p-4">
        <TesseraData giorni={giorni} />
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold leading-snug text-gray-900 group-hover:text-primary-700">{e.nome}</span>
          <span className="mt-0.5 block text-xs text-neutral-500">{periodo ?? 'Date da definire'}</span>
        </span>
      </span>
      <span className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
          <Users className="h-3.5 w-3.5" />
          {e.partecipanti} {e.partecipanti === 1 ? 'partecipante' : 'partecipanti'}
        </span>
        {giorni.length === 0 ? (
          <span className="text-[11px] font-medium text-amber-700">Programma da importare</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-600">
            <span className={cn('h-2 w-2 rounded-full', e.scelte_aperte ? 'bg-green-500' : 'bg-neutral-300')} />
            {e.scelte_aperte ? 'Richieste aperte' : 'Richieste chiuse'}
          </span>
        )}
      </span>
    </button>
  );
}

/** L'elenco degli eventi (congressi, convegni) e il pulsante per crearne uno. */
export default function EventiPage() {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [eventi, setEventi] = useState<EventoInElenco[]>([]);
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [nuovoAperto, setNuovoAperto] = useState(false);

  const carica = useCallback(async () => {
    setCaricando(true);
    try {
      setEventi(await eventiService.elenco());
      setErrore(null);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricando(false);
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  const crea = async (nome: string) => {
    try {
      const id = await eventiService.crea(nome);
      setNuovoAperto(false);
      navigate(`/eventi/${id}`);
    } catch (e) {
      addNotification({ type: 'error', title: "Non riesco a creare l'evento", message: messaggioErrore(e) });
    }
  };

  return (
    <div className="space-y-5 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Eventi</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Congressi e convegni: chi partecipa, che percorso sceglie e in quale aula si siede
          </p>
        </div>
        <Button onClick={() => setNuovoAperto(true)} className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo evento
        </Button>
      </div>

      {caricando ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : errore ? (
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-red-700 font-medium">Non riesco a leggere gli eventi</p>
            <p className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">{errore}</p>
            <Button variant="outline" className="mt-4" onClick={() => void carica()}>
              Riprova
            </Button>
          </CardContent>
        </Card>
      ) : eventi.length === 0 ? (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="py-14 text-center">
            <CalendarDays className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
            <p className="text-sm text-neutral-600 font-medium">Nessun evento ancora</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-md mx-auto leading-relaxed">
              Crea l'evento, importa il programma e scegli la lista dei partecipanti: ognuno riceverà il suo link per
              scegliere il percorso.
            </p>
            <Button className="mt-4" onClick={() => setNuovoAperto(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crea il primo evento
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {eventi.map((e) => (
            <SchedaEvento key={e.id} evento={e} onApri={() => navigate(`/eventi/${e.id}`)} />
          ))}
        </div>
      )}

      <NuovoEventoDialog aperta={nuovoAperto} onChiudi={() => setNuovoAperto(false)} onCrea={crea} />
    </div>
  );
}
